import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const teachers = [
  {
    name: '教师A',
    icon: 'ri-user-3-line',
    features: [
      { label: '案例使用', value: '情境案例 + 时事素材', strength: true },
      { label: '问题设计', value: '开放性提问链', strength: true },
      { label: '互动方式', value: '小组讨论 + 全班分享', strength: false },
      { label: '理论阐释', value: '案例→理论单向连接', strength: false },
      { label: '资源支持', value: '多媒体丰富', strength: true },
    ],
    color: 'primary' as const,
  },
  {
    name: '教师B',
    icon: 'ri-user-3-line',
    features: [
      { label: '案例使用', value: '经典文本案例', strength: false },
      { label: '问题设计', value: '递进追问结构', strength: true },
      { label: '互动方式', value: '苏格拉底式对话', strength: true },
      { label: '理论阐释', value: '严密的逻辑推演', strength: true },
      { label: '资源支持', value: '文献推荐丰富', strength: true },
    ],
    color: 'accent' as const,
  },
  {
    name: '教师C',
    icon: 'ri-user-3-line',
    features: [
      { label: '案例使用', value: '生活化案例，缺乏理论桥接', strength: false },
      { label: '问题设计', value: '基础问答为主', strength: false },
      { label: '互动方式', value: '学生参与积极但深度不足', strength: false },
      { label: '理论阐释', value: '理论讲解清晰但独立', strength: false },
      { label: '资源支持', value: '需要补充权威资料', strength: false },
    ],
    color: 'primary' as const,
  },
];

export default function P71TeachingGroup({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-6 md:py-8">
        <div ref={titleRef} className="text-center mb-4 md:mb-5">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />一教研组一图
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            从个体经验走向<span className="text-gold-600">共同教研</span>
          </h2>
          <p className={`text-sm text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            教研组不只看谁讲得好，更要看好在哪里、问题在哪里、经验怎样共享
          </p>
        </div>

        {/* Teacher Comparison */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 max-w-6xl mx-auto mb-3 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {teachers.map((t, ti) => (
            <div
              key={t.name}
              className="p-5 glass-liquid rounded-xl"
              style={{ transitionDelay: `${300 + ti * 150}ms` }}
            >
              <div className="flex items-center gap-3 mb-4 pb-3 border-b border-background-200/70">
                <div className={`w-10 h-10 rounded-xl ${t.color === 'accent' ? 'bg-gold-100/80 border border-gold-300/40' : 'bg-primary-100/60 border border-primary-200/40'} flex items-center justify-center`}>
                  <i className={`${t.icon} text-lg ${t.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`} />
                </div>
                <span className="font-heading text-base font-bold text-foreground-800">{t.name}</span>
              </div>
              <div className="space-y-2.5">
                {t.features.map((f, fi) => (
                  <div key={f.label} className="flex items-start gap-2" style={{ transitionDelay: `${500 + ti * 150 + fi * 60}ms` }}>
                    <i className={`text-xs mt-0.5 flex-shrink-0 ${f.strength ? 'ri-checkbox-circle-fill text-primary-600' : 'ri-checkbox-blank-circle-line text-foreground-300'}`} />
                    <div>
                      <span className="text-xs font-body font-bold text-foreground-800">{f.label}: </span>
                      <span className={`text-xs font-body ${f.strength ? 'text-foreground-600' : 'text-foreground-400'}`}>{f.value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Shareable Section */}
        <div className={`max-w-4xl mx-auto mb-3 transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="p-4 glass-gold border border-gold-200/40 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-share-forward-line text-sm text-gold-600" />
              <span className="text-xs font-body font-bold text-gold-700">可共享经验</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {['优秀案例', '追问方法', '理论结构模板'].map((item, i) => (
                <span
                  key={item}
                  className="px-3 py-1.5 rounded-full text-xs font-body font-semibold bg-gold-100/80 text-gold-700 border border-gold-300/30"
                  style={{ transitionDelay: `${600 + i * 80}ms` }}
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-5 glass-liquid rounded-xl">
            <p className="text-sm text-foreground-600 font-body leading-relaxed">
              AI的作用，是把分散在不同课堂中的个体经验和共同问题组织为<strong className="text-gold-700">可共享、可讨论、可迁移</strong>的教研资源。共性短板如导入过长、核心问题出现较晚、案例多但理论回扣不足，可以转化为集体备课的明确议题。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}